import { useEffect } from 'react';
import { Layout5Root } from './Layout5Root';
import './styles.css';
import './services.css';
import { LayoutHeader as Header } from '@/layouts';
import { useTheme } from '@/contexts/ThemeContext';
import { LayoutFooter as Footer } from '@/layouts';
import { ServiceModal } from '@/components/ServiceModal';
import { useAppData } from '@/contexts/AppDataContext';
import { openWhatsApp } from '@/lib/whatsapp';
import { useStore } from '@/contexts/StoreContext';
import {
  CreditCard,
  Shield,
  FileText,
  Video,
  MessageCircle,
  Wrench,
  TrendingUp,
  Search,
  Users,
  Package,
  Settings,
  Gauge,
  Handshake,
  HandCoins,
  Car,
  ArrowRight,
  Phone,
} from 'lucide-react';

const serviceInfo: Record<string, { icon: typeof Video; title: string; description: string }> = {
  '65da42048eca108d87c91da6': { icon: Video, title: 'Agendar Videochamada', description: 'Agende uma videochamada para conhecer nossos veículos sem sair de casa' },
  '65dcb7548eca108d87c91dbb': { icon: Search, title: 'Avaliação', description: 'Avaliação profissional do seu veículo com preço justo de mercado' },
  '65dcb7c58eca108d87c91dbd': { icon: HandCoins, title: 'Consignação', description: 'Deixe seu veículo em consignação conosco e venda com segurança' },
  '65dcb6528eca108d87c91db9': { icon: FileText, title: 'Despachante', description: 'Serviços de despachante para documentação veicular completa' },
  '65dcef468eca108d87c91dc8': { icon: Users, title: 'Consórcio', description: 'Adquira seu veículo através de consórcio com as melhores condições' },
  '65dcb7898eca108d87c91dbc': { icon: CreditCard, title: 'Financiamento', description: 'Análise de crédito e financiamento com as melhores taxas do mercado' },
  '6628fd535bda2ae51f6fc5ff': { icon: Package, title: 'Peças e Acessórios', description: 'Peças originais e acessórios para seu veículo' },
  '65dcef098eca108d87c91dc7': { icon: Wrench, title: 'Revisões', description: 'Revisões preventivas e corretivas com equipe especializada' },
  '65dcb7238eca108d87c91dba': { icon: Shield, title: 'Seguros', description: 'Cotação de seguros com as principais seguradoras do país' },
  '65dceeac8eca108d87c91dc6': { icon: Settings, title: 'Serviços', description: 'Serviços automotivos especializados para seu veículo' },
  '65dcb7fe8eca108d87c91dbe': { icon: TrendingUp, title: 'Tabela FIPE', description: 'Consulte o valor do seu veículo pela tabela FIPE' },
  '65dcb5e78eca108d87c91db8': { icon: Gauge, title: 'Test-Drive', description: 'Agende um test drive e experimente o veículo dos seus sonhos' },
  '65dcb8548eca108d87c91dbf': { icon: Handshake, title: 'Vendas Diretas', description: 'Vendas diretas com as melhores condições e atendimento personalizado' },
};

const getServiceInfo = (serviceId: string) => {
  return serviceInfo[serviceId] || {
    icon: MessageCircle,
    title: 'Serviço',
    description: 'Entre em contato para mais informações sobre este serviço',
  };
};

const getServiceButtonText = (serviceId: string) => {
  const buttonTexts: Record<string, string> = {
    '65da42048eca108d87c91da6': 'Agendar',
    '65dceeac8eca108d87c91dc6': 'Agendar',
    '65dcef098eca108d87c91dc7': 'Agendar',
    '65dcb5e78eca108d87c91db8': 'Agendar',
    '65dcb7898eca108d87c91dbc': 'Simular',
    '65dcb7548eca108d87c91dbb': 'Avaliar',
    '65dcb7c58eca108d87c91dbd': 'Avaliar',
    '65dcb7238eca108d87c91dba': 'Cotar',
    '65dcb6528eca108d87c91db9': 'Cotar',
    '6628fd535bda2ae51f6fc5ff': 'Cotar',
    '65dcef468eca108d87c91dc8': 'Solicitar',
    '65dcb8548eca108d87c91dbf': 'Solicitar',
    '65dcb7fe8eca108d87c91dbe': 'Consultar',
  };
  return buttonTexts[serviceId] || 'Solicitar';
};

const trustItems = [
  { icon: Shield, title: 'Veículos revisados', desc: 'Inspeção e histórico transparente' },
  { icon: CreditCard, title: 'Financiamento', desc: 'Condições especiais e aprovação ágil' },
  { icon: Car, title: 'Estoque completo', desc: 'Seminovos e zero KM disponíveis' },
  { icon: MessageCircle, title: 'Atendimento humano', desc: 'Equipe pronta no WhatsApp e telefone' },
];

const Services = () => {
  const { config } = useAppData();
  const { selectedStore } = useStore();
  const store = selectedStore || config.stores[0];
  const services = store.services || [];
  const { colors } = useTheme();
  const buttonColor = colors.button;
  const titleColor = colors.title;
  const textColor = colors.text;

  useEffect(() => {
    document.title = `Serviços - ${config.name}`;
  }, [config.name]);

  return (
    <Layout5Root withHeader className="wl5-services-page">
      <Header config={config} />

      <section className="wl5-services-hero">
        <div className="wl5-container">
          <div className="wl5-services-hero-grid">
            <div className="wl5-services-hero-copy">
              <p className="wl5-eyebrow">Serviços</p>
              <h1 className="wl5-services-hero-title" style={{ color: titleColor }}>
                Tudo para fechar negócio
              </h1>
              <p className="wl5-services-hero-lead" style={{ color: textColor }}>
                Financiamento, avaliação e muito mais em um só lugar. Escolha o serviço e fale com nossa equipe.
              </p>
            </div>

            <div className="wl5-services-hero-panel" style={{ borderColor: `${buttonColor}22` }}>
              <div
                className="wl5-services-hero-panel-accent"
                style={{ backgroundColor: buttonColor }}
                aria-hidden="true"
              />
              <p className="wl5-services-hero-panel-label">Atendimento</p>
              <p className="wl5-services-hero-panel-text" style={{ color: textColor }}>
                {services.length > 0
                  ? `${services.length} serviços disponíveis para você`
                  : 'Entre em contato para saber como podemos ajudar'}
              </p>
              <div className="wl5-services-hero-panel-actions">
                <button
                  type="button"
                  className="wl5-services-hero-chip"
                  style={{ borderColor: `${buttonColor}40`, color: buttonColor }}
                  onClick={() => openWhatsApp(store.whatsapp?.ios)}
                >
                  <MessageCircle className="h-4 w-4" />
                  WhatsApp
                </button>
                {store.phone?.formatted && (
                  <button
                    type="button"
                    className="wl5-services-hero-chip"
                    style={{ borderColor: `${buttonColor}40`, color: buttonColor }}
                    onClick={() => { window.location.href = `tel:${store.phone?.formatted}`; }}
                  >
                    <Phone className="h-4 w-4" />
                    Ligar
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="wl5-services-catalog">
        <div className="wl5-container">
          {services.length === 0 ? (
            <div className="wl5-services-empty">
              <MessageCircle className="h-9 w-9" style={{ color: textColor }} />
              <h2 className="wl5-services-empty-title" style={{ color: titleColor }}>
                Nenhum serviço disponível no momento
              </h2>
              <p className="wl5-services-empty-text" style={{ color: textColor }}>
                Entre em contato para saber como podemos ajudar.
              </p>
            </div>
          ) : (
            <div className="wl5-services-catalog-list">
              {services.map((service: { service: string }, index: number) => {
                const info = getServiceInfo(service.service);
                const IconComponent = info.icon;
                const actionLabel = getServiceButtonText(service.service);

                return (
                  <article key={`${service.service}-${index}`} className="wl5-service-catalog-card group">
                    <span className="wl5-service-catalog-index" style={{ color: buttonColor }}>
                      {String(index + 1).padStart(2, '0')}
                    </span>

                    <div
                      className="wl5-service-catalog-icon"
                      style={{ backgroundColor: `${buttonColor}12`, color: buttonColor }}
                    >
                      <IconComponent className="h-5 w-5" />
                    </div>

                    <div className="wl5-service-catalog-body">
                      <h2 className="wl5-service-catalog-title" style={{ color: titleColor }}>
                        {info.title}
                      </h2>
                      <p className="wl5-service-catalog-desc" style={{ color: textColor }}>
                        {info.description}
                      </p>
                    </div>

                    <ServiceModal service={service} serviceTitle={info.title} config={config}>
                      <button
                        type="button"
                        className="wl5-service-catalog-cta"
                        style={{ borderColor: buttonColor, color: buttonColor }}
                      >
                        {actionLabel}
                        <ArrowRight className="h-4 w-4" />
                      </button>
                    </ServiceModal>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <section className="wl5-services-trust">
        <div className="wl5-container">
          <div className="wl5-services-trust-head">
            <p className="wl5-eyebrow">Por que comprar aqui</p>
            <h2 className="wl5-services-trust-title" style={{ color: titleColor }}>
              Compra com confiança
            </h2>
          </div>

          <div className="wl5-services-trust-grid">
            {trustItems.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="wl5-services-trust-card">
                <div
                  className="wl5-services-trust-icon"
                  style={{ backgroundColor: `${buttonColor}12`, color: buttonColor }}
                >
                  <Icon className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="wl5-services-trust-card-title" style={{ color: titleColor }}>
                    {title}
                  </h3>
                  <p className="wl5-services-trust-card-desc" style={{ color: textColor }}>
                    {desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer config={config} />
    </Layout5Root>
  );
};

export default Services;
